import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Modal } from 'react-bootstrap';
import careTeamApi, { errorText } from './careTeamApi';
import AddPatientsDialog from './AddPatientsDialog';
import BulkCareTeamDialog from './BulkCareTeamDialog';
import UndoToast from './UndoToast';
import { removeConfirmText, slotBadges, plural } from './careTeamText';

// Nurse profile "Patients" tab: her patients with slot, Add patients, per-row Remove (Undo), Move all patients to…
const NurseCareTeamPatients = ({ nurseId, canEdit = true }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [adding, setAdding] = useState(false);
    const [bulk, setBulk] = useState(null);
    const [removeRow, setRemoveRow] = useState(null);
    const [toast, setToast] = useState(null);
    const load = useCallback(() => {
        if (!nurseId) return;
        careTeamApi.nursePatients(nurseId).then((d) => { setData(d); setError(''); }).catch((e) => setError(errorText(e)));
    }, [nurseId]);
    useEffect(() => { load(); }, [load]);
    const closeToast = useCallback(() => setToast(null), []);

    if (error) return <div className="alert alert-warning py-2">Patients: {error}</div>;
    if (!data) return <small>Loading patients…</small>;
    const nurse = data.nurse;
    const q = query.trim().toLowerCase();
    const rows = data.patients.filter((p) => !q || p.name.toLowerCase().includes(q) || String(p.emrId || '').toLowerCase().includes(q));
    const slotOf = (p) => (p.slots.length === 2 ? 'both' : p.slots[0]);
    const slotLabel = (p) => (p.slots.length === 2 ? 'RPM and CCM nurse' : `${p.slots[0].toUpperCase()} nurse`);

    const doRemove = async () => {
        const p = removeRow;
        setRemoveRow(null);
        try {
            const res = await careTeamApi.apply({ patientIds: [p.id], slot: slotOf(p), personId: null, fromPersonId: nurse.id, source: 'nurse' });
            const failed = res.results.find((r) => r.status !== 'ok');
            setToast(failed ? { message: `${p.name}: ${failed.reason}` } : { message: `${p.name} removed from ${nurse.name}`, batchId: res.batchId });
        } catch (e) {
            setToast({ message: errorText(e) });
        }
        load();
    };
    const undo = async (batchId) => {
        setToast(null);
        try {
            await careTeamApi.undo(batchId);
            setToast({ message: 'Change undone' });
        } catch (e) {
            setToast({ message: errorText(e) });
        }
        load();
    };

    return (
        <div data-testid="nurse-care-team-patients">
            <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: '8px' }}>
                <strong style={{ fontSize: '14px' }}>{plural(data.patients.length, 'patient')}</strong>
                <input type="search" className="form-control form-control-sm" style={{ maxWidth: '220px' }} placeholder="Search…" aria-label="Search nurse patients" value={query} onChange={(e) => setQuery(e.target.value)} />
                {canEdit && (
                    <>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>Add patients</button>
                        <button type="button" className="btn btn-outline-primary btn-sm" disabled={!data.patients.length} onClick={() => setBulk({ mode: 'move', patientIds: data.patients.map((p) => p.id) })}>
                            Move all patients to…
                        </button>
                    </>
                )}
            </div>
            {rows.length ? (
                <table className="table table-sm table-striped">
                    <thead><tr><th>Name</th><th>EHR-ID</th><th>Programs</th><th>Slot</th><th>Status</th>{canEdit && <th></th>}</tr></thead>
                    <tbody>
                        {rows.map((p) => (
                            <tr key={p.id}>
                                <td><Link className="link" to={{ pathname: '/patientProfile', state: { patientid: p.id } }}>{p.name}</Link></td>
                                <td>{p.emrId || '—'}</td>
                                <td>{p.programs.join(', ')}</td>
                                <td>{slotBadges(p.slots).map((b) => <Badge key={b} pill bg="primary text-white" className="mr-1">{b}</Badge>)}</td>
                                <td>{p.block ? <span style={{ color: 'red' }}>Deactivated</span> : <span style={{ color: 'green' }}>Active</span>}</td>
                                {canEdit && <td><button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setRemoveRow(p)}>Remove</button></td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : <p className="text-muted">No patients assigned.</p>}

            <AddPatientsDialog show={adding} nurse={nurse} onHide={() => setAdding(false)} onContinue={(ids) => { setAdding(false); setBulk({ mode: 'assign', patientIds: ids }); }} />
            <BulkCareTeamDialog
                show={!!bulk}
                mode={bulk?.mode || 'assign'}
                patientIds={bulk?.patientIds || []}
                fixedPerson={bulk?.mode === 'assign' ? nurse : undefined}
                fromPerson={bulk?.mode === 'move' ? nurse : undefined}
                source="nurse"
                onHide={() => setBulk(null)}
                onDone={load}
            />
            <Modal show={!!removeRow} onHide={() => setRemoveRow(null)} aria-labelledby="nurse-remove-title">
                <Modal.Header closeButton><Modal.Title id="nurse-remove-title" as="h6">Remove patient</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p data-testid="nurse-remove-text" style={{ fontSize: '14px' }}>
                        {removeRow && removeConfirmText({ fromName: nurse.name, slotLabel: slotLabel(removeRow), patientName: removeRow.name })}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setRemoveRow(null)}>Cancel</button>
                    <button type="button" className="btn btn-sm btn-danger" onClick={doRemove}>Remove</button>
                </Modal.Footer>
            </Modal>
            <UndoToast toast={toast} onUndo={undo} onClose={closeToast} />
        </div>
    );
};

export default NurseCareTeamPatients;
